import { useRef } from "react";
import * as Tone from "tone";
import { Scale, scaleDegreeGenerator } from "../constants/scales";
import { logger } from "./logger";
import { loopInterval, LoopStep } from "./loopSteps";
import { PlayerExternalState } from "./useExternalState/useExternalState";
import { MutatableInternalState } from "./useMutatableInternalState";
import generateRandomNumberBetweenTwoNums from "util/generateRandomNumberBetweenTwoNums";
import {
  TriggerAttackRelease,
  TriggerAttackReleasePlaybackParams,
} from "./useSynths";
import {
  getChordByDegree,
  getChordMidiNotes,
  getChordScaleDegrees,
} from "../constants/chords";
import { useEffectOnce } from "usehooks-ts";

interface Params {
  getInternalState: () => MutatableInternalState;
  updateExternalState: (state: Partial<PlayerExternalState>) => void;
  triggerAttackRelease: (params: TriggerAttackRelease) => void;
}

export default function useToneLoop({
  getInternalState,
  updateExternalState,
  triggerAttackRelease,
}: Params) {
  const toneLoop = useRef<Tone.Loop | null>(null);

  function getLoop() {
    if (toneLoop.current) {
      return toneLoop.current;
    }

    logger("useToneLoop: TONE LOOP CREATED");

    toneLoop.current = new Tone.Loop((time) => {
      logger("useToneLoop: TONE LOOP TIME:", time);

      const state = getInternalState();

      // store in scope before incrementing
      const step = state.steps[state.stepIndex];
      const scale = state.scales[state.scaleIndex];
      const synthParams = loopStepToTriggerAttackRelease(time, scale, step);

      // increment index
      state.stepIndex = getNextStepIndex(state);

      // if last step, increment scale
      if (step.lastStep) {
        state.scaleIndex = getNextScaleIndex(state);
      }

      Tone.Draw.schedule(() => {
        logger("useToneLoop: DRAW SCHEDULED", time);

        const scopedScale = scale;
        const scopedMelodyDegrees = synthParams.melody?.degrees ?? [];
        const scopedChordDegrees = synthParams.chord?.degrees ?? [];

        updateExternalState({
          activeScale: scopedScale,
          activeMelodyDegrees: new Set(scopedMelodyDegrees),
          activeChordDegrees: new Set(scopedChordDegrees),
        });
      }, time);

      triggerAttackRelease(synthParams);
    }, loopInterval).start(0);

    toneLoop.current.debug = true;

    return toneLoop.current;
  }

  useEffectOnce(() => {
    logger("useToneLoop: LOOP INITIATED");

    getLoop();
  });
}

function getNextStepIndex(state: MutatableInternalState): number {
  if (state.stepIndex + 1 >= state.steps.length) {
    return 0;
  }

  return state.stepIndex + 1;
}

function getNextScaleIndex(state: MutatableInternalState): number {
  // Don't change when hold scale
  if (state.holdScale) {
    return state.scaleIndex;
  }

  // If only one scale, don't change
  if (state.scales.length === 1) {
    return 0;
  }

  if (state.transpositionPattern === "up/down") {
    const isGoingUp = state.transpositionDirection === "up";
    const isAtScaleEnd = state.scaleIndex + 1 >= state.scales.length;
    const isAtScaleStart = state.scaleIndex - 1 < 0;

    if (isGoingUp && isAtScaleEnd) {
      state.transpositionDirection = "down";
      return state.scaleIndex - 1;
    }

    if (!isGoingUp && isAtScaleStart) {
      state.transpositionDirection = "up";
      return state.scaleIndex + 1;
    }

    return isGoingUp ? state.scaleIndex + 1 : state.scaleIndex - 1;
  }

  if (state.transpositionPattern === "random") {
    return generateRandomNumberBetweenTwoNums({
      min: 0,
      max: state.scales.length - 1,
    });
  }

  throw new Error("Invalid transposition pattern");
}

function loopStepToTriggerAttackRelease(
  time: number,
  scale: Scale,
  step: LoopStep
): TriggerAttackRelease {
  return {
    time,
    metrome: true,
    melody: stepToMelodyTriggerAttackReleasePlaybackParams(scale, step),
    chord: stepToChordTriggerAttackReleasePlaybackParams(scale, step),
  };
}

function stepToMelodyTriggerAttackReleasePlaybackParams(
  scale: Scale,
  step: LoopStep
): TriggerAttackReleasePlaybackParams | undefined {
  if (step.type === "rest") {
    return undefined;
  }

  if (!step.melody) {
    return undefined;
  }

  const noteGetter = scaleDegreeGenerator(scale.rootMidi, scale.type);
  const melodyNotes = step.melody.degreesToPlay.map((degree) =>
    noteGetter(degree)
  );

  return {
    midiNotes: melodyNotes,
    duration: step.melody.duration,
    degrees: step.melody.degreesToPlay,
  };
}

function stepToChordTriggerAttackReleasePlaybackParams(
  scale: Scale,
  step: LoopStep
): TriggerAttackReleasePlaybackParams | undefined {
  if (step.type === "rest") {
    return undefined;
  }

  if (!step.chord) {
    return undefined;
  }

  const chordType = getChordByDegree(scale.type, step.chord.scaleDegree);
  const chordMidiNotes = getChordMidiNotes(scale.rootMidi, chordType);

  return {
    midiNotes: chordMidiNotes,
    duration: step.chord.duration,
    degrees: getChordScaleDegrees(scale.type, step.chord.scaleDegree),
  };
}
